import { z } from 'zod';
import { MetaSchema, Rfc3339DateTime } from './create-chat.js';

/**
 * Shared BFF↔SPA contracts for workstream memory (ADR-0056) — what the agents
 * have learned about the user in one workstream, rendered on the Memory rail
 * and curated there by ACCEPT / FORGET.
 *
 * Every entry is agent-authored. The user never composes memory text, so no
 * user prose can reach an agent's system prompt through this store — the only
 * writes are "keep this" and "forget this", each carrying an opaque id and
 * nothing else.
 *
 * The BFF's memory routes invoke the `workstreamagent`
 * (`listMemory` / `acceptMemory` / `deleteMemory`, `workstream-agent.ts`),
 * narrow the agent's tolerant-out reply into these shapes, and return them in
 * the standard `{ data, meta }` envelope.
 *
 * `data` schemas are **strict** (the BFF authors them; a stray key is a bug);
 * the outer envelopes are **loose** (`z.looseObject`, ADR-0024 §11 — a future
 * additive field never breaks an old client).
 */

/** Longest a single memory fact may be — mirrors the Python
 * `MAX_MEMORY_FACT_CHARS`, which re-truncates server-side regardless. */
export const MAX_MEMORY_FACT_CHARS = 280;

/**
 * One thing the workstream has learned. `id` is the OPAQUE per-entry handle the
 * SPA accepts/forgets by — the agent resolves it against THIS workstream's
 * memory list, so an id from elsewhere simply doesn't resolve (the same
 * scope-by-resolution property ADR-0046 gave `artifactId`).
 *
 * `source` names the proposing agent. `accepted` is the user's "keep this": it
 * pins the entry against the store's newest-N cap and marks it as confirmed in
 * the agent prompt.
 */
export const MemoryEntrySchema = z.strictObject({
  id: z.string().min(1).max(128),
  fact: z.string().min(1).max(MAX_MEMORY_FACT_CHARS),
  source: z.string().max(128),
  accepted: z.boolean(),
  createdAt: Rfc3339DateTime,
});
export type MemoryEntry = z.infer<typeof MemoryEntrySchema>;

/** `data` of `GET /api/v1/workstreams/{id}/memory`. */
export const ListMemoryDataSchema = z.strictObject({
  memory: z.array(MemoryEntrySchema),
});
export type ListMemoryData = z.infer<typeof ListMemoryDataSchema>;

export const ListMemoryResponseSchema = z.looseObject({
  data: ListMemoryDataSchema,
  meta: MetaSchema,
});
export type ListMemoryResponse = z.infer<typeof ListMemoryResponseSchema>;

/** `data` of an accept — the resulting entry. */
export const MemoryEntryDataSchema = z.strictObject({
  entry: MemoryEntrySchema,
});
export type MemoryEntryData = z.infer<typeof MemoryEntryDataSchema>;

export const MemoryEntryResponseSchema = z.looseObject({
  data: MemoryEntryDataSchema,
  meta: MetaSchema,
});
export type MemoryEntryResponse = z.infer<typeof MemoryEntryResponseSchema>;

/** `data` of `DELETE /api/v1/workstreams/{id}/memory/{entryId}` — the deleted id (idempotent). */
export const DeleteMemoryDataSchema = z.strictObject({
  id: z.string().min(1).max(128),
});
export type DeleteMemoryData = z.infer<typeof DeleteMemoryDataSchema>;

export const DeleteMemoryResponseSchema = z.looseObject({
  data: DeleteMemoryDataSchema,
  meta: MetaSchema,
});
export type DeleteMemoryResponse = z.infer<typeof DeleteMemoryResponseSchema>;
